<template>
  <section class="pb-2">
    <div
      class="d-flex justify-content-end mb-2"
    >
      <div v-if="loading">
        <b-skeleton
          type="input"
          width="255px"
          height="34px"
        />
      </div>
      <div v-else>
        <SearchInputField
          v-if="audiences.length || filter.name"
          class="mb-0"
          :placeholder="$tk('ClientPortal.Audiences.SearchByNamePlaceholder')"
          @onInputSearch="updateFilters"
        />
      </div>
    </div>
    <div
      v-if="loading"
      class="bg-white"
    >
      <b-skeleton-table
        :rows="10"
        :columns="5"
        :table-props="{ bordered: true, striped: true }"
      />
    </div>
    <b-card
      v-show="!loading"
      no-body
      class="mb-0 audience-table"
    >
      <div
        v-if="!audiences.length && !filter.name && !loadingNext"
        class="d-flex h-100 align-items-center justify-content-center"
      >
        <b-col class="text-center">
          <GjIcon
            size="64px"
            class="mb-2"
            name="AvatarGroup"
          />
          <b-col
            class="mb-50 "
          >
            {{ $tk('ClientPortal.Audiences.YouCanCreateAudiencesByUsingGjirafaTechDMPBisko') }}
            <b-row class="justify-content-center">
              {{ $tk('ClientPortal.Audiences.ClickHereToGetStarted') }}
              <b-link
                class="ml-50"
                href="https://admin.bisko.giraffa.tech"
                target="blank"
              >
                {{ 'https://admin.bisko.giraffa.tech' }}
              </b-link>
            </b-row>
          </b-col>
        </b-col>
      </div>
      <b-table
        v-else
        ref="my-table"
        sticky-header="78vh"
        :items="audiences"
        :fields="fields"
        :filter="filter"
        :busy.sync="loadingNext"
        :no-border-collapse="true"
        class="mb-0 client-table__data"
        show-empty
        :empty-text="$tk('ClientPortal.Audiences.NoAudiencesHaveBeenAddedYet')"
        @scroll.native="onScroll"
      >
        <template #empty>
          <div
            class="d-flex justify-content-center"
          >
            <b-spinner
              v-if="loadingNext"
              variant="primary"
            />
            <b-col
              v-else
              class="text-center"
            >
              <GjIcon
                size="64px"
                class="mb-2"
                name="AvatarGroup"
              />
              <div>
                {{ $tk('ClientPortal.Audiences.NoAudiencesMatchThisSearch') }}
              </div>
            </b-col>
          </div>
        </template>
        <template #cell(name)="data">
          <div class="d-flex align-items-end">
            <span
              :ref="'name-'+data.item.id"
              class="ellipsis"
            >
              {{ data.item.name }}
            </span>
            <b-button
              v-if="checkTextWidth('name-'+data.item.id)"
              :ref="'name-eclipsis-'+data.item.id"
              variant="link"
              size="sm"
              class="text-secondary underline"
              @click="toggleShowMore('name-'+data.item.id, 'name-eclipsis-'+data.item.id)"
            >
              {{ $tk('ClientPortal.Audiences.More') }}
            </b-button>
          </div>
        </template>
        <template #cell(code)="data">
          <div class="d-flex align-items-end">
            <div
              :ref="'code-'+data.item.id"
              class="ellipsis word-wrap"
            >
              {{ data.item.code }}
            </div>
            <b-button
              v-if="checkTextWidth('code-'+data.item.id)"
              :ref="'code-eclipsis-'+data.item.id"
              variant="link"
              size="sm"
              class="text-secondary underline"
              @click="toggleShowMore('code-'+data.item.id, 'code-eclipsis-'+data.item.id)"
            >
              {{ $tk('ClientPortal.Audiences.More') }}
            </b-button>
          </div>
        </template>
        <template #cell(description)="data">
          <div class="d-flex align-items-end">
            <div
              :ref="'description-'+data.item.id"
              class="ellipsis"
            >
              {{ data.item.description }}
            </div>
            <b-button
              v-if="checkTextWidth('description-'+data.item.id)"
              :ref="'description-eclipsis-'+data.item.id"
              variant="link"
              size="sm"
              class="text-secondary underline"
              @click="toggleShowMore('description-'+data.item.id, 'description-eclipsis-'+data.item.id)"
            >
              {{ $tk('ClientPortal.Audiences.More') }}
            </b-button>
          </div>
        </template>
        <template #cell(details)="data">
          <div class="d-flex align-items-end">
            <div
              :ref="'details-'+data.item.id"
              class="ellipsis"
            >
              <div
                v-if="data.item.insertedByUser != null"
              >
                <span class="pr-50">
                  {{ $tk('ClientPortal.Audience.CreatedBy') }}</span>
                <b-avatar
                  v-b-tooltip.hover.top="`${data.item.insertedByUser.displayName}`"
                  variant="transparent"
                  class="avatar"
                  size="20px"
                  :src="data.item.insertedByUser.avatar"
                />
                {{ $tk('ClientPortal.Audience.At') }} {{ formatDate(data.item.insertedByUser.dateTime) }}
              </div>
              <div
                v-if="data.item.updatedByUser != null"
                class="mt-25"
              >
                {{ $tk('ClientPortal.Audience.UpdatedBy') }}
                <b-avatar
                  v-b-tooltip.hover.top="`${data.item.updatedByUser.displayName}`"
                  variant="transparent"
                  class="avatar"
                  size="20px"
                  :src="data.item.updatedByUser.avatar"
                />
                {{ $tk('ClientPortal.Audience.At') }} {{ formatDate(data.item.updatedByUser.dateTime) }}
              </div>
            </div>
          </div>
        </template>
        <template #cell(usersCount)="data">
          <div class="d-flex justify-content-between">
            <span>
              {{ data.item.usersCount | formatUsers }}
            </span>
            <b-link
              v-if="data.item.typeId == 3 || data.item.typeId == 2"
              class="text-secondary"
              @click="showDeleteAudienceDialog(data.item.id, data.index)"
            >
              <GjIcon name="Delete" />
            </b-link>
          </div>
        </template>
      </b-table>
    </b-card>
    <b-modal
      ref="deleteAudienceModal"
      :title="$tk('ClientPortal.Audience.DeleteHeaderMessage')"
      centered
    >
      {{ $tk('ClientPortal.Audience.AreYouSureYouWantToDeleteThisAudience') }}
      <template v-slot:modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="deleteAudienceModal.loading"
          size="sm"
          @click="confirmDeleteAudience"
        >
          {{ $tk('ClientPortal.Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import AudiencesService from '@/api/client/audiences.service'
import notification from '@/mixins/toastification'
import SearchInputField from '@/components/SearchInputField.vue'
import vueDebounce from 'vue-debounce'
import Vue from 'vue'

Vue.use(vueDebounce)
Vue.use(vueDebounce, {
  listenTo: 'input',
  defaultTime: '700ms',
})

export default {
  components: {
    GjIcon,
    SearchInputField,
  },
  filters: {
    formatUsers(value) {
      if (value < 1e3) return value
      if (value >= 1e3 && value < 1e6) return `${+(value / 1e3).toFixed(1)}K`
      if (value >= 1e6 && value < 1e9) return `${+(value / 1e6).toFixed(1)}M`
      if (value >= 1e9 && value < 1e12) return `${+(value / 1e9).toFixed(1)}B`
      return value
    },
  },
  mixins: [notification],
  data() {
    return {
      audiences: [],
      statuses: {},
      loading: false,
      filter: {
        page: -1,
        name: '',
        status: [],
      },
      loadingNext: false,
      completed: false,
      deleteAudienceModal: {
        loading: false,
        audienceId: 0,
        index: 0,
      },
    }
  },
  computed: {
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Audience.Name'),
          sortable: true,
          tdClass: 'table-name-cell',
        },
        {
          key: 'code',
          label: this.$tk('ClientPortal.Audience.Code'),
          tdClass: 'table-code-cell',
          sortable: true,
        },
        {
          key: 'description',
          label: this.$tk('ClientPortal.Audience.Description'),
          sortable: true,
          tdClass: 'table-description-cell',
        },
        {
          key: 'details',
          label: this.$tk('ClientPortal.Audience.Details'),
          tdClass: 'table-details-cell',
          sortable: true,
        },
        {
          key: 'usersCount',
          label: this.$tk('ClientPortal.Audience.UserCount'),
          sortable: true,
        },
      ]
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    this.loading = true
    this.loadAudiences()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.filter.page = -1
      this.filter.name = ''
      this.audiences = []
      this.loadAudiences()
    },
    async loadAudiences() {
      if (this.loadingNext) { return }

      this.loadingNext = true
      try {
        // eslint-disable-next-line no-plusplus
        const response = await AudiencesService.getAudiences(++this.filter.page, this.filter.name, '', this.$route.params.accountId)
        if (response.audiences.length) {
          // eslint-disable-next-line no-plusplus
          this.audiences.push(...response.audiences)
        } else { this.completed = true }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.loadingNext = false
      }
      this.$nextTick(() => { this.loading = false })
    },
    updateFilters(searchTerm) {
      this.filter.name = searchTerm
      this.loadingNext = false
      this.filter.page = -1
      this.completed = false
      this.audiences = []
      this.loadAudiences()
    },
    onScroll(event) {
      if (!this.completed) {
        if (event.target.scrollTop + event.target.clientHeight + 5 >= event.target.scrollHeight) {
          this.loadAudiences()
        }
      }
    },
    showDeleteAudienceDialog(audienceId, index) {
      this.deleteAudienceModal.audienceId = audienceId
      this.deleteAudienceModal.index = index
      this.$refs.deleteAudienceModal.show()
    },
    async confirmDeleteAudience() {
      this.deleteAudienceModal.loading = true
      try {
        const response = await AudiencesService.deleteAudience(this.deleteAudienceModal.audienceId, this.$route.params.accountId)
        if (response.saved) {
          this.audiences.splice(this.deleteAudienceModal.index, 1)
          this.success(response.message)
        } else { this.error(response.message) }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.deleteAudienceModal.loading = false
        this.$refs.deleteAudienceModal.hide()
      }
    },
    toggleShowMore(id, buttonId) {
      const content = this.$refs[id]
      const button = this.$refs[buttonId]
      if (content.classList.contains('ellipsis')) {
        content.classList.remove('ellipsis')
        button.innerHTML = this.$tk('ClientPortal.Audiences.Less')
      } else {
        content.classList.add('ellipsis')
        button.innerHTML = this.$tk('ClientPortal.Audiences.More')
      }
    },
    checkTextWidth(text) {
      if (this.$refs[text]) {
        const element = this.$refs[text]
        return this.isEllipsisActive(element)
      }
      return false
    },
    isEllipsisActive(element) {
      return element.offsetWidth < element.scrollWidth
    },
  },
  setup() {
    const formatDate = date => date.slice(0, date.length - 5)
    return {
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/views/client/properties/audiences.scss';
</style>
